import type {PayloadAction} from '@reduxjs/toolkit';
import {createSlice} from '@reduxjs/toolkit';
import type {EventWizardReduxState} from '@Components/event-wizard/event-wizard.types';
import {EventWizardProgressStep, EventWizardSourceType, orderedProgressSteps, RegistrationMode} from '@Components/event-wizard/event-wizard.types';
import type {RecurringEvent} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {RecurringType} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.types';
import {WEEKDAY_NUMS} from '@Components/event-wizard/month-day-selectors/month-day-selectors.types';
import {ALL_WEEKDAYS_USA, Weekdays} from '@Components/event-wizard/week-day-selectors/week-day-selectors.types';
import {addHoursToDate} from '@Components/event-wizard/event-wizard.helper';
import type {EventWizardVenue} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import {EventVenueType} from '@Components/event-wizard/event-wizard-venue/event-wizard-venue.types';
import type {EventType, EventTypes} from '@Components/event-wizard/type-dropdown/type-dropdown.types';
import type {TimeZone} from 'timezones-list';
import type {PlaceDetails} from '@Components/google-map/google-map.types';
import type {EventRegistrationFields} from '@Components/events/events.types';
import type {EventWizardOrganisers, OrganiserDetails} from '@Components/event-wizard/event-wizard-organisers/event-wizard-organisers.types';
import type {CoverPhoto} from '@Components/event-wizard/event-wizard-image-video/event-wizard-image-video.types';
import type {EventColorScheme, EventStyles} from '@Components/event-wizard/event-style/event-style.types';
import {StyleVariant} from '@Components/event-wizard/event-style/event-style.types';
import {getDefaultLegibilityMemo} from '@Libraries/event-colors.library';
import type {LegibilityMemo} from '@Libraries/event-colors.library';
import {PMW_COLORS_PRIMARY} from '@Utils/color.util';
import {getEventColorScheme} from '@Components/event-wizard/event-style/event-style.helper';
import {initSocialMediaProfileLinks} from '@Components/social-media-profile-links/social-media-profile-links-library';
import {DateTimeMobilePanel} from '@Panels/event-wizard-date-time-panel/event-wizard-date-time-panel.types';
import {shouldUpsellUserForTeamShare} from '@Components/event-wizard/event-wizard-other-options/event-wizard-other-options.types';
import {getMaxRecurringDate} from '@Components/event-wizard/recurring-event-selector/recurring-event-selector.helper';
import {DEFAULT_EVENTS_STYLE_FONT_FAMILY, getButtonTextOptionsForMode, getUserDefaultTimeZone, shouldUpsellUserForRegistrationOptions} from '@Libraries/events.library';
import {isEventTicketingCountrySupported, isPaidTicketingFeatureAvailable} from '@Libraries/event-ticketing.library';
import {isTicketingSelectionEligible} from '@Components/event-wizard/event-wizard-ticketing/ticketing-gating.helper';
import type {TicketingConfig, TicketingFeeParams, TicketingQuestion, QuestionBelongsTo} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {getDefaultTicketingConfig} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {resolveRegistrationModeState} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';

const getDefaultEventTypes = (): EventTypes => {
  return {
    allTypes: [],
  };
};

/**
 * Writes a registration mode into the wizard state: the two ticketing flags plus the legacy
 * `areRegistrationsRequired`, which only Custom Link needs — RSVP and Tickets ARE the registration, and None
 * takes none. Shared by the hydration and the tile-click reducers so a mode can never be half-applied.
 *
 * The legacy flag has to be written on hydration too, not just on a tile click. An event saved before
 * ticketing existed stores it as true, and opening it resolves to the RSVP tile; leaving the flag alone would
 * save that event as RSVP-plus-legacy-registrations — a combination no new event can produce, and one that
 * hands the nav bar's Register button the `js-register-btn` class, so it opens the old email signup form while
 * the page's own CTA opens the RSVP dialog.
 */
const applyRegistrationModeToState = (state: EventWizardReduxState, mode: RegistrationMode): RegistrationMode => {
  const modeState = resolveRegistrationModeState(mode, state.recurringEvent.isRecurring);

  state.ticketingConfig.isSelected = modeState.isSelected;
  state.ticketingConfig.isRsvp = modeState.isRsvp;
  state.registrationDetails.areRegistrationsRequired = modeState.areRegistrationsRequired;
  state.selectedRegistrationMode = modeState.mode;

  return modeState.mode;
};

export const getDefaultRecurringEvent = (): RecurringEvent => {
  const defaultRecurringType = RecurringType.DAILY;
  const defaultStartDateTime = getDefaultStartDateTime();

  return {
    isRecurring: false,
    recurringType: defaultRecurringType,
    recurringEndDate: getMaxRecurringDate(defaultStartDateTime, defaultRecurringType),
    recurringMonthly: {
      weekdayNum: WEEKDAY_NUMS[0],
      weekday: Weekdays.MONDAY,
    },
    recurringWeeks: [ALL_WEEKDAYS_USA[defaultStartDateTime.getDay()]],
  };
};

export const getDefaultTimeZone = (): TimeZone => {
  return getUserDefaultTimeZone();
};

export const getDefaultRegistrationDetails = (): EventRegistrationFields => {
  return {
    areRegistrationsRequired: false,
    email: true,
    name: !shouldUpsellUserForRegistrationOptions(),
    phone: false,
    registerBtnText: getButtonTextOptionsForMode(RegistrationMode.CUSTOM_LINK)[0], // seeds the Custom Link CTA (preview reads it directly, so it can't start blank)
  };
};

export const getDefaultEventTeamShareDetails = (): boolean => {
  return !shouldUpsellUserForTeamShare();
};

export const getDefaultOrganiserDetails = (): EventWizardOrganisers => {
  return {
    allOrganisers: [],
    selectedOrganiser: getDefaultSelectedOrganiserDetails(),
  };
};

export const getDefaultSelectedOrganiserDetails = (): OrganiserDetails => {
  return {
    hashedId: undefined,
    name: '',
    email: '',
    phone: undefined,
    profilePic: '',
    isProfilePicturePrefilled: false,
    socialMediaLinks: initSocialMediaProfileLinks(),
  };
};

const getDefaultStyles = (): EventStyles => {
  return {
    colors: getEventColorScheme({background: null, contentColor: PMW_COLORS_PRIMARY.PRIMARY_100}, StyleVariant.BALANCED),
    fontFamily: DEFAULT_EVENTS_STYLE_FONT_FAMILY,
  };
};

const getDefaultVenue = (): EventWizardVenue => {
  return {
    selectedVenueType: EventVenueType.OFFLINE,
  };
};

export const getDefaultStartDateTime = (): Date => {
  const defaultDaysAhead = 15;
  const defaultHoursAhead = 1;

  const startDate = new Date();
  startDate.setDate(startDate.getDate() + defaultDaysAhead);
  startDate.setHours(startDate.getHours() + defaultHoursAhead);
  startDate.setMinutes(0);
  startDate.setSeconds(0);
  startDate.setMilliseconds(0);
  return startDate;
};

export const getDefaultEndDateTime = (): Date => {
  return addHoursToDate(getDefaultStartDateTime(), 6);
};

/**
 * The default step-2 tile for a NEW event. Tickets is preferred, but it is gated (paid-ticketing feature +
 * supported country + premium non-trial) — literally the predicate the Tickets tile and the footer use —
 * so ineligible organisers fall back to RSVP rather than landing pre-selected on a tile they can't use.
 * Only affects new events; loading an existing event overrides the mode via deriveSelectedRegistrationMode.
 */
const getDefaultRegistrationMode = (): RegistrationMode => {
  return isTicketingSelectionEligible(isPaidTicketingFeatureAvailable(), isEventTicketingCountrySupported()) ? RegistrationMode.TICKETS : RegistrationMode.RSVP;
};

const defaultRegistrationMode = getDefaultRegistrationMode();

const eventWizardInitialState: EventWizardReduxState = {
  eventHID: undefined,
  googleMapAPIKey: '',
  title: '',
  types: getDefaultEventTypes(),
  description: '',
  isDescriptionUserMade: true,
  isAIDescriptionGenerating: false,
  startDateTime: getDefaultStartDateTime(),
  endDateTime: getDefaultEndDateTime(),
  timeZone: getDefaultTimeZone(),
  recurringEvent: getDefaultRecurringEvent(),
  showCountdown: true,
  venue: getDefaultVenue(),
  userPastVenues: [],
  registrationDetails: getDefaultRegistrationDetails(),
  ticketingConfig: {...getDefaultTicketingConfig(), isSelected: true, isRsvp: defaultRegistrationMode === RegistrationMode.RSVP},
  ticketingFees: null,
  selectedRegistrationMode: defaultRegistrationMode,
  isRemoveWatermark: false,
  isSharedInTeam: getDefaultEventTeamShareDetails(),
  isSharedAsGoogleEvent: true,
  organisers: getDefaultOrganiserDetails(),
  coverPhoto: {},
  shouldCheckIfInErrState: false,
  styles: getDefaultStyles(),
  styleLegibility: getDefaultLegibilityMemo(),
  teamName: undefined,
  currentProgressStep: orderedProgressSteps[0],
  isEventBeingCreated: false,
  shouldNextBtnBeDisabled: false,
  shouldUpdateExpandedReviewSection: false,
  dateTimeMobilePanel: DateTimeMobilePanel.DEFAULT,
  pageSourceType: EventWizardSourceType.DEFAULT,
  isDesignPrefillContentLoading: false,
  progressStepLandedOn: EventWizardProgressStep.OVERVIEW,
  doesPAYGUserHaveAITemplateLimit: false,
  isStylesPanelOpen: false,
  shouldUpsellRecurring: false,
  shouldShowRecurringEventDetails: false,
  isTicketingCountrySupported: true,
  isPaidTicketingFeatureAvailable: false,
  notifyMeEmail: '',
};
const eventWizardSlice = createSlice({
  name: 'eventWizardSlice',
  initialState: eventWizardInitialState,
  reducers: {
    onEventWizardEventHIDChange: (state, action: PayloadAction<string>): void => {
      state.eventHID = action.payload;
    },
    onEventWizardGoogleMapAPIKeyChange: (state, action: PayloadAction<string>): void => {
      state.googleMapAPIKey = action.payload;
    },
    onEventWizardTitleChange: (state, action: PayloadAction<string>): void => {
      state.title = action.payload;
    },
    onEventWizardAllTypesChange: (state, action: PayloadAction<EventType[]>): void => {
      state.types.allTypes = action.payload;
    },
    onEventWizardSelectedTypeChange: (state, action: PayloadAction<EventType | undefined>): void => {
      state.types.selectedType = action.payload;
    },
    onEventWizardAITypeChange: (state, action: PayloadAction<EventType | undefined>): void => {
      state.types.aiType = action.payload;
    },
    onEventWizardIsAIEventTypeLoading: (state, action: PayloadAction<boolean>): void => {
      state.types.isAITypeLoading = action.payload;
    },
    onEventWizardDescriptionChange: (state, action: PayloadAction<string>): void => {
      state.description = action.payload;
    },
    onEventWizardStartDateTimeUpdate: (state, action: PayloadAction<Date>): void => {
      state.startDateTime = action.payload;
    },
    onEventWizardEndDateTimeUpdate: (state, action: PayloadAction<Date>): void => {
      state.endDateTime = action.payload;
    },
    onEventWizardTimeZoneUpdate: (state, action: PayloadAction<TimeZone>): void => {
      state.timeZone = action.payload;
    },
    onEventWizardToggleCountdown: (state, action: PayloadAction<boolean>): void => {
      state.showCountdown = action.payload;
    },
    onEventWizardRecurringChange: (state, action: PayloadAction<RecurringEvent>): void => {
      state.recurringEvent = action.payload;
    },
    // Re-resolves the current mode under the new recurrence value instead of discarding it. RSVP survives in
    // both directions; Tickets converts to RSVP, so turning Repeat on keeps the organiser collecting
    // registrations rather than silently leaving the event with no way to register at all.
    // isRecurring has to land first — applyRegistrationModeToState resolves against it.
    onEventWizardToggleRecurringChange: (state, action: PayloadAction<boolean>): void => {
      state.recurringEvent.isRecurring = action.payload;
      applyRegistrationModeToState(state, state.selectedRegistrationMode);
    },
    onEventWizardRecurringTypeChange: (state, action: PayloadAction<RecurringType>): void => {
      state.recurringEvent.recurringType = action.payload;
    },
    onEventWizardRecurringEndsOnChange: (state, action: PayloadAction<Date>): void => {
      state.recurringEvent.recurringEndDate = action.payload;
    },
    onEventWizardRecurringMonthWeekdayOccurenceChange: (state, action: PayloadAction<string>): void => {
      state.recurringEvent.recurringMonthly.weekdayNum = action.payload;
    },
    onEventWizardRecurringMonthWeekdayChange: (state, action: PayloadAction<Weekdays>): void => {
      state.recurringEvent.recurringMonthly.weekday = action.payload;
    },
    onEventWizardRecurringWeeklyChange: (state, action: PayloadAction<Weekdays[]>): void => {
      state.recurringEvent.recurringWeeks = action.payload;
    },
    onEventWizardVenueTypeChange: (state, action: PayloadAction<EventVenueType | undefined>): void => {
      state.venue.selectedVenueType = action.payload;
    },
    onEventWizardUserPastOfflineVenueChange: (state, action: PayloadAction<PlaceDetails[]>): void => {
      state.userPastVenues = action.payload;
    },
    onEventWizardOfflineVenueChange: (state, action: PayloadAction<PlaceDetails | undefined>): void => {
      if (action.payload) {
        state.venue.selectedVenueType = EventVenueType.OFFLINE;
        state.venue.offlineVenue = action.payload;
        return;
      }
      state.venue.selectedVenueType = undefined;
      state.venue.offlineVenue = undefined;
    },
    onEventWizardOnlineVenueChange: (state, action: PayloadAction<string | undefined>): void => {
      if (action.payload && action.payload !== '') {
        state.venue.selectedVenueType = EventVenueType.ONLINE;
        state.venue.onlineVenueURL = action.payload;
        return;
      }
      state.venue.selectedVenueType = undefined;
      state.venue.onlineVenueURL = undefined;
    },
    onEventWizardRegistrationChange: (state, action: PayloadAction<EventRegistrationFields>): void => {
      state.registrationDetails = action.payload;
    },
    onEventWizardEventRegistrationChange: (state, action: PayloadAction<EventRegistrationFields>): void => {
      state.registrationDetails = {
        ...state.registrationDetails,
        areRegistrationsRequired: action.payload.areRegistrationsRequired,
        email: action.payload.email,
        name: action.payload.name,
        phone: action.payload.phone,
        registerBtnText: action.payload.registerBtnText,
        registerBtnLink: action.payload.registerBtnLink,
      };
    },
    onEventWizardRemoveWatermarkToggle: (state, action: PayloadAction<boolean>): void => {
      state.isRemoveWatermark = action.payload;
    },
    onEventWizardShareInTeamToggle: (state, action: PayloadAction<boolean>): void => {
      state.isSharedInTeam = action.payload;
    },
    onEventWizardShareAsGoogleEventToggle: (state, action: PayloadAction<boolean>): void => {
      state.isSharedAsGoogleEvent = action.payload;
    },
    onEventWizardOrganiserUpdate: (state, action: PayloadAction<EventWizardOrganisers>): void => {
      state.organisers = action.payload;
    },
    onEventWizardSelectedOrganiserUpdate: (state, action: PayloadAction<OrganiserDetails>): void => {
      state.organisers.selectedOrganiser = action.payload;
    },
    onEventWizardCoverPhotoUpdate: (state, action: PayloadAction<CoverPhoto>): void => {
      state.coverPhoto = action.payload;
    },
    onEventWizardIsCoverPhotoPreviewLoading: (state, action: PayloadAction<boolean>): void => {
      state.coverPhoto.isPreviewLoading = action.payload;
    },
    onEventWizardCheckErrorStateUpdate: (state, action: PayloadAction<boolean>): void => {
      state.shouldCheckIfInErrState = action.payload;
    },
    onEventWizardStyleUpdate: (state, action: PayloadAction<EventStyles>): void => {
      state.styles = action.payload;
    },
    onEventWizardStyleFontUpdate: (state, action: PayloadAction<string>): void => {
      state.styles.fontFamily = action.payload;
    },
    onEventWizardStyleColorUpdate: (state, action: PayloadAction<EventColorScheme>): void => {
      state.styles.colors = action.payload;
    },
    onEventWizardStyleLegibilityUpdate: (state, action: PayloadAction<LegibilityMemo>): void => {
      state.styleLegibility = action.payload;
    },
    onEventWizardTeamNameUpdate: (state, action: PayloadAction<string | undefined>): void => {
      state.teamName = action.payload;
    },
    onEventWizardProgressStepUpdate: (state, action: PayloadAction<EventWizardProgressStep>): void => {
      state.currentProgressStep = action.payload;
    },
    onEventWizardIsEventBeingCreatedUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isEventBeingCreated = action.payload;
    },
    onEventWizardShouldNextBtnBeDisabled: (state, action: PayloadAction<boolean>): void => {
      state.shouldNextBtnBeDisabled = action.payload;
    },
    onEventWizardUpdateShouldExpandReviewSection: (state, action: PayloadAction<boolean>): void => {
      state.shouldUpdateExpandedReviewSection = action.payload;
    },
    onEventWizardDateTimeMobilePanelUpdate: (state, action: PayloadAction<DateTimeMobilePanel>): void => {
      state.dateTimeMobilePanel = action.payload;
    },
    onEventWizardPageSourceTypeUpdate: (state, action: PayloadAction<EventWizardSourceType>): void => {
      state.pageSourceType = action.payload;
    },
    onEventWizardIsDesignPrefillContentLoadingUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isDesignPrefillContentLoading = action.payload;
    },
    onEventWizardProgressStepLandedOnUpdate: (state, action: PayloadAction<EventWizardProgressStep>): void => {
      state.progressStepLandedOn = action.payload;
    },
    onEventWizardIsDescriptionUserMadeUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isDescriptionUserMade = action.payload;
    },
    onEventWizardIsAIDescriptionGeneratingUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isAIDescriptionGenerating = action.payload;
    },
    onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate: (state, action: PayloadAction<boolean>): void => {
      state.doesPAYGUserHaveAITemplateLimit = action.payload;
    },
    onEventWizardIsStylesPanelOpenUpdate: (state, action: PayloadAction<boolean>): void => {
      state.isStylesPanelOpen = action.payload;
    },
    onEventWizardShouldUpsellRecurring: (state, action: PayloadAction<boolean>): void => {
      state.shouldUpsellRecurring = action.payload;
    },
    onEventWizardUpdateShouldShowRecurringEventDetails: (state, action: PayloadAction<boolean>): void => {
      state.shouldShowRecurringEventDetails = action.payload;
    },
    // Hydration: applies the mode a loaded event resolves to. Keeps the event's stored link even when the
    // resolved mode does not use it, so a tile click can still offer it back rather than an empty field.
    onEventWizardRegistrationModeChange: (state, action: PayloadAction<RegistrationMode>): void => {
      applyRegistrationModeToState(state, action.payload);
    },
    onEventWizardRegistrationModeSelect: (state, action: PayloadAction<RegistrationMode>): void => {
      const resolvedMode = applyRegistrationModeToState(state, action.payload);

      if (resolvedMode !== RegistrationMode.CUSTOM_LINK) {
        state.registrationDetails.registerBtnLink = undefined;
      }
    },
    onEventWizardTicketingConfigHydrate: (state, action: PayloadAction<TicketingConfig>): void => {
      state.ticketingConfig = action.payload;
    },
    onEventWizardTicketingPriceChange: (state, action: PayloadAction<number>): void => {
      state.ticketingConfig.price = action.payload;
    },
    onEventWizardTicketingTotalTicketsChange: (state, action: PayloadAction<number | null>): void => {
      state.ticketingConfig.totalTickets = action.payload;
    },
    onEventWizardTicketingMaxPerOrderChange: (state, action: PayloadAction<number | null>): void => {
      state.ticketingConfig.maxPerOrder = action.payload;
    },
    onEventWizardTicketingPassFeesToggle: (state, action: PayloadAction<boolean>): void => {
      state.ticketingConfig.passFeesToBuyer = action.payload;
    },
    onEventWizardTicketingTaxChange: (state, action: PayloadAction<Partial<Pick<TicketingConfig, 'taxLabel' | 'taxRatePercent' | 'taxMode'>>>): void => {
      Object.assign(state.ticketingConfig, action.payload);
    },
    onEventWizardTicketingFeeParamsLoaded: (state, action: PayloadAction<TicketingFeeParams>): void => {
      state.ticketingFees = action.payload;
    },
    onEventWizardTicketingGatingPropsUpdate: (state, action: PayloadAction<{isCountrySupported: boolean; isPaidTicketingAvailable: boolean; notifyMeEmail: string}>): void => {
      state.isTicketingCountrySupported = action.payload.isCountrySupported;
      state.isPaidTicketingFeatureAvailable = action.payload.isPaidTicketingAvailable;
      state.notifyMeEmail = action.payload.notifyMeEmail;
    },
    onEventWizardTicketingButtonTextChange: (state, action: PayloadAction<string>): void => {
      // Tickets and RSVP share this picker but keep separate stored values so each tile remembers its own
      // choice; isRsvp (set on tile select) picks the target field.
      if (state.ticketingConfig.isRsvp) {
        state.ticketingConfig.rsvpButtonText = action.payload;
      } else {
        state.ticketingConfig.buttonText = action.payload;
      }
    },
    onEventWizardTicketingWhoRespondsChange: (state, action: PayloadAction<QuestionBelongsTo>): void => {
      state.ticketingConfig.whoResponds = action.payload;
    },
    onEventWizardTicketingSectionTitleChange: (state, action: PayloadAction<string>): void => {
      state.ticketingConfig.questionSectionTitle = action.payload;
    },
    onEventWizardTicketingSectionDescriptionChange: (state, action: PayloadAction<string>): void => {
      state.ticketingConfig.questionSectionDescription = action.payload;
    },
    onEventWizardTicketingQuestionAdd: (state, action: PayloadAction<TicketingQuestion>): void => {
      state.ticketingConfig.questions.push(action.payload);
    },
    onEventWizardTicketingQuestionUpdate: (state, action: PayloadAction<{index: number; question: TicketingQuestion}>): void => {
      state.ticketingConfig.questions[action.payload.index] = action.payload.question;
    },
    onEventWizardTicketingQuestionRemove: (state, action: PayloadAction<number>): void => {
      state.ticketingConfig.questions.splice(action.payload, 1);
    },
    onEventWizardTicketingQuestionsReorder: (state, action: PayloadAction<{sourceIndex: number; destinationIndex: number}>): void => {
      const [movedQuestion] = state.ticketingConfig.questions.splice(action.payload.sourceIndex, 1);
      state.ticketingConfig.questions.splice(action.payload.destinationIndex, 0, movedQuestion);
    },
  },
});

export const {
  onEventWizardEventHIDChange,
  onEventWizardGoogleMapAPIKeyChange,
  onEventWizardTitleChange,
  onEventWizardAllTypesChange,
  onEventWizardSelectedTypeChange,
  onEventWizardDescriptionChange,
  onEventWizardStartDateTimeUpdate,
  onEventWizardEndDateTimeUpdate,
  onEventWizardTimeZoneUpdate,
  onEventWizardRecurringChange,
  onEventWizardToggleRecurringChange,
  onEventWizardRecurringTypeChange,
  onEventWizardRecurringEndsOnChange,
  onEventWizardRecurringMonthWeekdayChange,
  onEventWizardRecurringMonthWeekdayOccurenceChange,
  onEventWizardRecurringWeeklyChange,
  onEventWizardToggleCountdown,
  onEventWizardUserPastOfflineVenueChange,
  onEventWizardVenueTypeChange,
  onEventWizardOfflineVenueChange,
  onEventWizardOnlineVenueChange,
  onEventWizardRegistrationChange,
  onEventWizardEventRegistrationChange,
  onEventWizardRemoveWatermarkToggle,
  onEventWizardShareInTeamToggle,
  onEventWizardShareAsGoogleEventToggle,
  onEventWizardOrganiserUpdate,
  onEventWizardSelectedOrganiserUpdate,
  onEventWizardCoverPhotoUpdate,
  onEventWizardIsCoverPhotoPreviewLoading,
  onEventWizardCheckErrorStateUpdate,
  onEventWizardStyleUpdate,
  onEventWizardStyleFontUpdate,
  onEventWizardStyleColorUpdate,
  onEventWizardStyleLegibilityUpdate,
  onEventWizardTeamNameUpdate,
  onEventWizardProgressStepUpdate,
  onEventWizardIsEventBeingCreatedUpdate,
  onEventWizardShouldNextBtnBeDisabled,
  onEventWizardUpdateShouldExpandReviewSection,
  onEventWizardPageSourceTypeUpdate,
  onEventWizardDateTimeMobilePanelUpdate,
  onEventWizardIsDesignPrefillContentLoadingUpdate,
  onEventWizardProgressStepLandedOnUpdate,
  onEventWizardIsDescriptionUserMadeUpdate,
  onEventWizardIsAIDescriptionGeneratingUpdate,
  onEventWizardDoesPAYGUserHaveAITemplateLimitUpdate,
  onEventWizardIsStylesPanelOpenUpdate,
  onEventWizardIsAIEventTypeLoading,
  onEventWizardShouldUpsellRecurring,
  onEventWizardUpdateShouldShowRecurringEventDetails,
  onEventWizardRegistrationModeChange,
  onEventWizardRegistrationModeSelect,
  onEventWizardTicketingConfigHydrate,
  onEventWizardTicketingPriceChange,
  onEventWizardTicketingTotalTicketsChange,
  onEventWizardTicketingMaxPerOrderChange,
  onEventWizardTicketingPassFeesToggle,
  onEventWizardTicketingTaxChange,
  onEventWizardTicketingFeeParamsLoaded,
  onEventWizardTicketingGatingPropsUpdate,
  onEventWizardTicketingButtonTextChange,
  onEventWizardTicketingWhoRespondsChange,
  onEventWizardTicketingSectionTitleChange,
  onEventWizardTicketingSectionDescriptionChange,
  onEventWizardTicketingQuestionAdd,
  onEventWizardTicketingQuestionUpdate,
  onEventWizardTicketingQuestionRemove,
  onEventWizardTicketingQuestionsReorder,
} = eventWizardSlice.actions;
export const eventWizardReducer = eventWizardSlice.reducer;
